<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';
import ProjectBaselines from './ProjectBaselines.vue';

import MilestonesAPI from 'dashboard/api/projectMilestones';
import RisksAPI from 'dashboard/api/projectRisks';
import StatusReportsAPI from 'dashboard/api/projectStatusReports';
import NotesAPI from 'dashboard/api/projectNotes';
import DocumentsAPI from 'dashboard/api/projectDocuments';

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  projectId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['navigate', 'open-phase']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// --- Static token maps -------------------------------------------------------
const RAG_DOT = {
  green: 'bg-n-teal-9',
  amber: 'bg-n-amber-9',
  red: 'bg-n-ruby-9',
  unknown: 'bg-n-slate-7',
};
const RAG_TEXT = {
  green: 'text-n-teal-11',
  amber: 'text-n-amber-11',
  red: 'text-n-ruby-11',
  unknown: 'text-n-slate-10',
};
const RAG_BG = {
  green: 'bg-n-teal-3',
  amber: 'bg-n-amber-3',
  red: 'bg-n-ruby-3',
  unknown: 'bg-n-alpha-1',
};

const STATUS_CLASS = {
  draft: 'bg-n-alpha-1 text-n-slate-11',
  active: 'bg-n-teal-3 text-n-teal-11',
  on_hold: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-blue-3 text-n-blue-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};

const HEALTH_OPTIONS = ['unknown', 'green', 'amber', 'red'];

// Phase lifecycle segment fill colour by status.
const PHASE_STATUS_COLOR = {
  not_started: 'bg-n-slate-5',
  in_progress: 'bg-n-brand',
  blocked: 'bg-n-ruby-9',
  completed: 'bg-n-teal-9',
  skipped: 'bg-n-slate-4',
};

const MILESTONE_TONE = {
  pending: 'slate',
  achieved: 'teal',
  missed: 'ruby',
  at_risk: 'amber',
};

const TONE_DOT = {
  slate: 'bg-n-slate-7',
  teal: 'bg-n-teal-9',
  amber: 'bg-n-amber-9',
  ruby: 'bg-n-ruby-9',
  blue: 'bg-n-blue-9',
};
const TONE_TEXT = {
  slate: 'text-n-slate-10',
  teal: 'text-n-teal-11',
  amber: 'text-n-amber-11',
  ruby: 'text-n-ruby-11',
  blue: 'text-n-blue-11',
};

const SCORE_WEIGHT = { low: 1, medium: 2, high: 3 };

const PROCESS_GROUP_LABELS = {
  initiating: 'PROJECTS.PHASES.PROCESS_GROUPS.INITIATING',
  planning: 'PROJECTS.PHASES.PROCESS_GROUPS.PLANNING',
  executing: 'PROJECTS.PHASES.PROCESS_GROUPS.EXECUTING',
  monitoring_controlling: 'PROJECTS.PHASES.PROCESS_GROUPS.MONITORING',
  closing: 'PROJECTS.PHASES.PROCESS_GROUPS.CLOSING',
};

// --- Fan-out data (resources not present in the show payload) -----------------
const milestones = ref([]);
const risks = ref([]);
const reports = ref([]);
const notes = ref([]);
const documents = ref([]);
const isLoading = ref(true);

const loadAll = async () => {
  isLoading.value = true;
  const [m, r, sr, n, d] = await Promise.allSettled([
    MilestonesAPI.get(props.projectId),
    RisksAPI.get(props.projectId),
    StatusReportsAPI.get(props.projectId),
    NotesAPI.get(props.projectId),
    DocumentsAPI.get(props.projectId),
  ]);
  milestones.value = m.status === 'fulfilled' ? m.value.data || [] : [];
  risks.value = r.status === 'fulfilled' ? r.value.data || [] : [];
  reports.value = sr.status === 'fulfilled' ? sr.value.data || [] : [];
  notes.value = n.status === 'fulfilled' ? n.value.data || [] : [];
  documents.value = d.status === 'fulfilled' ? d.value.data || [] : [];
  isLoading.value = false;
};

// --- Date helpers ------------------------------------------------------------
// Payloads mix epoch-seconds (notes) and ISO strings (reports, documents).
const toDate = value => {
  if (value === null || value === undefined || value === '') return null;
  const date =
    typeof value === 'number'
      ? new Date(value < 1e12 ? value * 1000 : value)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const fmtDate = value => {
  const date = toDate(value);
  if (!date) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const daysUntil = value => {
  const date = toDate(value);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date - today) / 86400000);
};

// Relative-due descriptor for a target date → { label, tone }.
const dueDescriptor = (value, statusTone = 'slate') => {
  const days = daysUntil(value);
  if (days === null) return { label: '—', tone: 'slate' };
  if (days < 0) {
    return {
      label: t('PROJECTS.OVERVIEW.OVERDUE_BY', { count: -days }),
      tone: 'ruby',
    };
  }
  if (days === 0)
    return { label: t('PROJECTS.OVERVIEW.DUE_TODAY'), tone: 'amber' };
  const tone = days <= 7 ? 'amber' : statusTone;
  return { label: t('PROJECTS.OVERVIEW.DUE_IN', { count: days }), tone };
};

// --- Project meta ------------------------------------------------------------
// Counts already shown by the parent metric strip (progress, deliverables,
// milestones, open risks, days-to-target) are deliberately not repeated here.
const summary = computed(() => props.project?.summary || {});

const statusKey = computed(() => props.project?.status || 'draft');
const statusClass = computed(
  () => STATUS_CLASS[statusKey.value] || STATUS_CLASS.draft
);
const statusLabel = computed(() =>
  t(`PROJECTS.STATUS.${statusKey.value.toUpperCase()}`)
);

const healthKey = computed(() => props.project?.health || 'unknown');
const priorityLabel = computed(() => {
  const p = props.project?.priority;
  return p ? t(`PROJECTS.PRIORITY.${p.toUpperCase()}`) : '—';
});

const empresa = computed(() => props.project?.empresa || null);
const ownerName = computed(
  () => props.project?.owner?.name || props.project?.owner_name || '—'
);
const sponsorName = computed(
  () => props.project?.sponsor?.name || props.project?.sponsor_name || '—'
);
const targetDate = computed(() => props.project?.target_end_date || '');
const budgetLabel = computed(() => {
  const b = props.project?.budget_amount;
  if (b === null || b === undefined || b === '') return '—';
  const amount = new Intl.NumberFormat().format(Number(b));
  return props.project?.budget_currency
    ? `${amount} ${props.project.budget_currency}`
    : amount;
});

// --- Executive RAG band (latest status report) -------------------------------
const latestReport = computed(() => {
  if (!reports.value.length) return null;
  return [...reports.value].sort((a, b) => {
    const av = toDate(a.reported_at || a.created_at)?.getTime() || 0;
    const bv = toDate(b.reported_at || b.created_at)?.getTime() || 0;
    return bv - av;
  })[0];
});

const ragItems = computed(() => {
  const report = latestReport.value;
  return [
    {
      key: 'overall',
      label: t('PROJECTS.STATUS_REPORTS.FIELDS.OVERALL_HEALTH'),
      health: report?.overall_health || healthKey.value || 'unknown',
    },
    {
      key: 'schedule',
      label: t('PROJECTS.STATUS_REPORTS.FIELDS.SCHEDULE_HEALTH'),
      health: report?.schedule_health || 'unknown',
    },
    {
      key: 'cost',
      label: t('PROJECTS.STATUS_REPORTS.FIELDS.COST_HEALTH'),
      health: report?.cost_health || 'unknown',
    },
    {
      key: 'scope',
      label: t('PROJECTS.STATUS_REPORTS.FIELDS.SCOPE_HEALTH'),
      health: report?.scope_health || 'unknown',
    },
  ];
});

const reportAsOf = computed(() => {
  const report = latestReport.value;
  if (!report) return null;
  return fmtDate(
    report.reported_at || report.reporting_period_end || report.created_at
  );
});

const openIssues = computed(() => summary.value.open_issues ?? 0);

// --- Phase lifecycle ---------------------------------------------------------
const orderedPhases = computed(() => {
  const list = props.project?.phases || [];
  return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const phaseSegments = computed(() => {
  const list = orderedPhases.value;
  const totalWeight =
    list.reduce((sum, p) => sum + (Number(p.weight) || 1), 0) || 1;
  return list.map(phase => {
    const tasks = (phase.stages || []).reduce(
      (sum, stage) => sum + (stage.tasks_count || 0),
      0
    );
    return {
      id: phase.id,
      name: phase.name,
      status: phase.status || 'not_started',
      isGate: Boolean(phase.is_gate),
      gateStatus: phase.gate_status || 'not_required',
      group: phase.process_group
        ? t(PROCESS_GROUP_LABELS[phase.process_group] || phase.process_group)
        : '',
      tasks,
      width: `${((Number(phase.weight) || 1) / totalWeight) * 100}%`,
      raw: phase,
    };
  });
});

const currentPhaseId = computed(() => {
  const active = orderedPhases.value.find(p => p.status === 'in_progress');
  return active?.id ?? null;
});

// --- Upcoming milestones -----------------------------------------------------
const upcomingMilestones = computed(() =>
  [...milestones.value]
    .filter(m => m.status !== 'achieved')
    .sort((a, b) => {
      const av = toDate(a.target_date)?.getTime() ?? Infinity;
      const bv = toDate(b.target_date)?.getTime() ?? Infinity;
      return av - bv;
    })
    .slice(0, 4)
    .map(m => {
      const baseTone = MILESTONE_TONE[m.status] || 'slate';
      const due =
        m.status === 'missed'
          ? { label: t('PROJECTS.MILESTONES.STATUS.MISSED'), tone: 'ruby' }
          : dueDescriptor(m.target_date, baseTone);
      return { ...m, due };
    })
);

// --- Top risks ---------------------------------------------------------------
const topRisks = computed(() =>
  [...risks.value]
    .filter(r => ['open', 'mitigating'].includes(r.status))
    .map(r => {
      const score =
        (SCORE_WEIGHT[r.probability] || 0) * (SCORE_WEIGHT[r.impact] || 0);
      let tone = 'teal';
      if (score >= 6) tone = 'ruby';
      else if (score >= 3) tone = 'amber';
      return { ...r, score, tone };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
);

// --- Recent activity (notes + documents merged) ------------------------------
const recentActivity = computed(() => {
  const noteItems = notes.value.map(n => ({
    key: `note-${n.id}`,
    type: 'note',
    icon: 'i-lucide-sticky-note',
    text: n.content,
    meta: n.user?.name || n.author_name || t('PROJECTS.NOTES.UNKNOWN_AUTHOR'),
    at: n.created_at,
    tab: 'notes',
  }));
  const docItems = documents.value.map(d => ({
    key: `doc-${d.id}`,
    type: 'doc',
    icon: 'i-lucide-file-text',
    text: d.filename,
    meta: t('PROJECTS.TABS.DOCUMENTS'),
    at: d.created_at,
    tab: 'documents',
  }));
  return [...noteItems, ...docItems]
    .sort(
      (a, b) => (toDate(b.at)?.getTime() || 0) - (toDate(a.at)?.getTime() || 0)
    )
    .slice(0, 5);
});

// --- Team --------------------------------------------------------------------
const members = computed(() => props.project?.members || []);
const memberCount = computed(
  () => props.project?.members_count ?? members.value.length
);
const contactCount = computed(() => props.project?.contacts_count ?? 0);

// --- Actions -----------------------------------------------------------------
const navigate = tab => emit('navigate', tab);
const openPhase = phase => emit('open-phase', phase?.raw ?? phase);

const updateHealth = async event => {
  await store.dispatch('updateProject', {
    id: props.projectId,
    health: event.target.value,
  });
};

const openEmpresa = () => {
  if (!empresa.value?.id) return;
  router.push({
    name: 'empresa_details',
    params: { empresaId: empresa.value.id },
  });
};

onMounted(loadAll);
</script>

<template>
  <div class="flex flex-col gap-5 xl:flex-row xl:items-start">
    <!-- Main column: the dashboard -->
    <div class="flex flex-col flex-1 min-w-0 gap-5">
      <!-- Executive RAG band -->
      <section
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center justify-between gap-3">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-activity size-3.5" />
            {{ t('PROJECTS.OVERVIEW.HEALTH_BAND') }}
          </h3>
          <span v-if="reportAsOf" class="text-xs text-n-slate-10">
            {{ t('PROJECTS.OVERVIEW.AS_OF', { date: reportAsOf }) }}
          </span>
          <button
            v-else
            type="button"
            class="text-xs text-n-blue-text hover:underline"
            @click="navigate('overview')"
          >
            {{ t('PROJECTS.OVERVIEW.NO_STATUS_REPORT') }}
          </button>
        </div>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div
            v-for="item in ragItems"
            :key="item.key"
            class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg"
            :class="RAG_BG[item.health] || RAG_BG.unknown"
          >
            <span
              class="rounded-full size-2.5 shrink-0"
              :class="RAG_DOT[item.health] || RAG_DOT.unknown"
            />
            <div class="flex flex-col min-w-0">
              <span class="text-xs text-n-slate-11">{{ item.label }}</span>
              <span
                class="text-sm font-medium capitalize"
                :class="RAG_TEXT[item.health] || RAG_TEXT.unknown"
              >
                {{
                  t(
                    `PROJECTS.HEALTH.${(item.health || 'unknown').toUpperCase()}`
                  )
                }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Phase lifecycle -->
      <section
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center justify-between gap-3">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-milestone size-3.5" />
            {{ t('PROJECTS.OVERVIEW.LIFECYCLE') }}
          </h3>
          <button
            type="button"
            class="text-xs text-n-blue-text hover:underline"
            @click="navigate('phases')"
          >
            {{ t('PROJECTS.OVERVIEW.VIEW_ALL') }}
          </button>
        </div>

        <ProjectEmptyState
          v-if="!phaseSegments.length"
          compact
          icon="i-lucide-list-ordered"
          :title="t('PROJECTS.PHASES.EMPTY')"
        />
        <template v-else>
          <!-- Segmented bar -->
          <div class="flex w-full gap-1 overflow-hidden">
            <button
              v-for="seg in phaseSegments"
              :key="seg.id"
              v-tooltip.top="`${seg.name}${seg.group ? ` · ${seg.group}` : ''}`"
              type="button"
              class="relative h-2 rounded-full transition-opacity hover:opacity-80"
              :class="[
                PHASE_STATUS_COLOR[seg.status] ||
                  PHASE_STATUS_COLOR.not_started,
                seg.id === currentPhaseId
                  ? 'ring-2 ring-offset-1 ring-n-brand ring-offset-n-solid-2'
                  : '',
              ]"
              :style="{ width: seg.width }"
              @click="openPhase(seg)"
            />
          </div>
          <!-- Legend chips -->
          <div class="flex flex-wrap gap-x-4 gap-y-1.5">
            <button
              v-for="seg in phaseSegments"
              :key="`label-${seg.id}`"
              type="button"
              class="flex items-center gap-1.5 text-xs text-n-slate-11 hover:text-n-slate-12"
              @click="openPhase(seg)"
            >
              <span
                class="rounded-full size-2 shrink-0"
                :class="
                  PHASE_STATUS_COLOR[seg.status] ||
                  PHASE_STATUS_COLOR.not_started
                "
              />
              <span class="truncate max-w-[140px]">{{ seg.name }}</span>
              <span
                v-if="seg.isGate"
                v-tooltip.top="t('PROJECTS.PHASES.GATE')"
                class="i-lucide-flag-triangle-right size-3 text-n-amber-11"
              />
              <span class="tabular-nums text-n-slate-9">{{ seg.tasks }}</span>
            </button>
          </div>
        </template>
      </section>

      <!-- 3-up: milestones / risks / latest status -->
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <!-- Upcoming milestones -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center justify-between gap-3">
            <h3
              class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              <span class="i-lucide-flag size-3.5" />
              {{ t('PROJECTS.OVERVIEW.UPCOMING_MILESTONES') }}
            </h3>
            <button
              type="button"
              class="text-xs text-n-blue-text hover:underline shrink-0"
              @click="navigate('milestones')"
            >
              {{ t('PROJECTS.OVERVIEW.VIEW_ALL') }}
            </button>
          </div>
          <p
            v-if="!upcomingMilestones.length"
            class="py-3 text-sm text-n-slate-10"
          >
            {{ t('PROJECTS.OVERVIEW.NO_UPCOMING_MILESTONES') }}
          </p>
          <ul v-else class="flex flex-col divide-y divide-n-weak/60">
            <li
              v-for="m in upcomingMilestones"
              :key="m.id"
              class="flex items-start gap-2.5 py-2.5"
            >
              <span
                class="mt-1 rounded-full size-2 shrink-0"
                :class="TONE_DOT[m.due.tone] || TONE_DOT.slate"
              />
              <div class="flex flex-col min-w-0 flex-1">
                <span class="flex items-center gap-1.5 text-sm text-n-slate-12">
                  <span class="truncate">{{ m.name }}</span>
                  <span
                    v-if="m.is_gate"
                    v-tooltip.top="t('PROJECTS.MILESTONES.GATE')"
                    class="i-lucide-flag-triangle-right size-3 text-n-amber-11 shrink-0"
                  />
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ fmtDate(m.target_date) }}
                </span>
              </div>
              <span
                class="text-xs font-medium shrink-0"
                :class="TONE_TEXT[m.due.tone] || TONE_TEXT.slate"
              >
                {{ m.due.label }}
              </span>
            </li>
          </ul>
        </section>

        <!-- Top risks -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center justify-between gap-3">
            <h3
              class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              <span class="i-lucide-triangle-alert size-3.5" />
              {{ t('PROJECTS.OVERVIEW.TOP_RISKS') }}
            </h3>
            <div class="flex items-center gap-2 shrink-0">
              <span
                v-if="openIssues"
                class="px-1.5 py-0.5 text-xs rounded-md tabular-nums"
                :class="RAG_BG.red + ' text-n-ruby-11'"
              >
                {{
                  t('PROJECTS.OVERVIEW.OPEN_ISSUES_COUNT', {
                    count: openIssues,
                  })
                }}
              </span>
              <button
                type="button"
                class="text-xs text-n-blue-text hover:underline"
                @click="navigate('risks')"
              >
                {{ t('PROJECTS.OVERVIEW.VIEW_ALL') }}
              </button>
            </div>
          </div>
          <p v-if="!topRisks.length" class="py-3 text-sm text-n-slate-10">
            {{ t('PROJECTS.OVERVIEW.NO_OPEN_RISKS') }}
          </p>
          <ul v-else class="flex flex-col divide-y divide-n-weak/60">
            <li
              v-for="risk in topRisks"
              :key="risk.id"
              class="flex items-start gap-2.5 py-2.5"
            >
              <span
                class="grid mt-px text-xs font-semibold rounded size-5 shrink-0 place-items-center tabular-nums"
                :class="[
                  TONE_TEXT[risk.tone],
                  RAG_BG[
                    risk.tone === 'ruby'
                      ? 'red'
                      : risk.tone === 'amber'
                        ? 'amber'
                        : 'green'
                  ],
                ]"
              >
                {{ risk.score }}
              </span>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-sm truncate text-n-slate-12">
                  {{ risk.title }}
                </span>
                <span class="text-xs capitalize text-n-slate-10">
                  {{
                    t(
                      `PROJECTS.RISKS.STRATEGIES.${(risk.response_strategy || 'mitigate').toUpperCase()}`
                    )
                  }}
                  ·
                  {{
                    t(
                      `PROJECTS.RISKS.STATUSES.${(risk.status || 'open').toUpperCase()}`
                    )
                  }}
                </span>
              </div>
            </li>
          </ul>
        </section>

        <!-- Latest status narrative -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2 md:col-span-2 xl:col-span-1"
        >
          <div class="flex items-center justify-between gap-3">
            <h3
              class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              <span class="i-lucide-clipboard-list size-3.5" />
              {{ t('PROJECTS.OVERVIEW.LATEST_REPORT') }}
            </h3>
          </div>
          <p v-if="!latestReport" class="py-3 text-sm text-n-slate-10">
            {{ t('PROJECTS.STATUS_REPORTS.EMPTY') }}
          </p>
          <div v-else class="flex flex-col gap-3">
            <p
              v-if="latestReport.summary"
              class="text-sm text-n-slate-12 line-clamp-3"
            >
              {{ latestReport.summary }}
            </p>
            <div
              v-if="latestReport.accomplishments"
              class="flex items-start gap-2"
            >
              <span
                class="i-lucide-check size-3.5 mt-0.5 text-n-teal-11 shrink-0"
              />
              <p class="text-xs text-n-slate-11 line-clamp-2">
                {{ latestReport.accomplishments }}
              </p>
            </div>
            <div v-if="latestReport.upcoming" class="flex items-start gap-2">
              <span
                class="i-lucide-arrow-right size-3.5 mt-0.5 text-n-blue-11 shrink-0"
              />
              <p class="text-xs text-n-slate-11 line-clamp-2">
                {{ latestReport.upcoming }}
              </p>
            </div>
            <div v-if="latestReport.blockers" class="flex items-start gap-2">
              <span
                class="i-lucide-octagon-alert size-3.5 mt-0.5 text-n-ruby-11 shrink-0"
              />
              <p class="text-xs text-n-slate-11 line-clamp-2">
                {{ latestReport.blockers }}
              </p>
            </div>
          </div>
        </section>
      </div>

      <!-- 2-up: team / activity -->
      <div class="grid gap-4 md:grid-cols-2">
        <!-- Team & stakeholders -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center justify-between gap-3">
            <h3
              class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              <span class="i-lucide-users size-3.5" />
              {{ t('PROJECTS.OVERVIEW.TEAM_STAKEHOLDERS') }}
            </h3>
            <button
              type="button"
              class="text-xs text-n-blue-text hover:underline shrink-0"
              @click="navigate('people')"
            >
              {{ t('PROJECTS.OVERVIEW.VIEW_ALL') }}
            </button>
          </div>
          <div v-if="memberCount || contactCount" class="flex flex-col gap-3">
            <div v-if="members.length" class="flex items-center -space-x-2">
              <Avatar
                v-for="member in members.slice(0, 8)"
                :key="member.id"
                v-tooltip.top="member.user?.name"
                :name="member.user?.name || '?'"
                :src="member.user?.thumbnail"
                :size="28"
                class="ring-2 ring-n-solid-2 rounded-full"
              />
              <span
                v-if="memberCount > 8"
                class="grid text-xs rounded-full size-7 place-items-center bg-n-alpha-2 text-n-slate-11 ring-2 ring-n-solid-2"
              >
                +{{ memberCount - 8 }}
              </span>
            </div>
            <div class="flex items-center gap-4 text-sm text-n-slate-11">
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-user-cog size-4 text-n-slate-10" />
                {{ t('PROJECTS.OVERVIEW.TEAM_COUNT', { count: memberCount }) }}
              </span>
              <button
                type="button"
                class="flex items-center gap-1.5 hover:text-n-slate-12"
                @click="navigate('people')"
              >
                <span class="i-lucide-contact size-4 text-n-slate-10" />
                {{
                  t('PROJECTS.OVERVIEW.STAKEHOLDER_COUNT', {
                    count: contactCount,
                  })
                }}
              </button>
            </div>
          </div>
          <p v-else class="py-3 text-sm text-n-slate-10">
            {{ t('PROJECTS.TEAM.EMPTY') }}
          </p>
        </section>

        <!-- Recent activity -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-history size-3.5" />
            {{ t('PROJECTS.OVERVIEW.RECENT_ACTIVITY') }}
          </h3>
          <p v-if="!recentActivity.length" class="py-3 text-sm text-n-slate-10">
            {{ t('PROJECTS.OVERVIEW.NO_ACTIVITY') }}
          </p>
          <ul v-else class="flex flex-col gap-2.5">
            <li
              v-for="item in recentActivity"
              :key="item.key"
              class="flex items-start gap-2.5"
            >
              <span
                :class="item.icon"
                class="size-4 mt-0.5 text-n-slate-10 shrink-0"
              />
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-sm truncate text-n-slate-12">
                  {{ item.text }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ item.meta }} · {{ fmtDate(item.at) }}
                </span>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <!-- Description (shown in the header on large screens) -->
      <section
        class="flex flex-col gap-1.5 p-4 border rounded-xl border-n-weak bg-n-solid-2 lg:hidden"
      >
        <h3
          class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          <span class="i-lucide-align-left size-3.5" />
          {{ t('PROJECTS.OVERVIEW.DESCRIPTION') }}
        </h3>
        <p class="text-sm whitespace-pre-line text-n-slate-12">
          {{ project.description || t('PROJECTS.OVERVIEW.NO_DESCRIPTION') }}
        </p>
      </section>
    </div>

    <!-- Right rail: details + baselines -->
    <aside class="w-full xl:w-72 shrink-0">
      <div class="flex flex-col gap-4 xl:sticky xl:top-4">
        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-info size-3.5" />
            {{ t('PROJECTS.OVERVIEW.DETAILS') }}
          </h3>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('PROJECTS.OVERVIEW.STATUS') }}
            </span>
            <span
              class="inline-flex items-center px-2 py-0.5 text-xs rounded-md"
              :class="statusClass"
            >
              {{ statusLabel }}
            </span>
          </div>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('PROJECTS.OVERVIEW.HEALTH') }}
            </span>
            <span class="flex items-center gap-1.5 text-n-slate-12">
              <span
                class="rounded-full size-2.5 shrink-0"
                :class="RAG_DOT[healthKey] || RAG_DOT.unknown"
              />
              <select
                :value="healthKey"
                :aria-label="t('PROJECTS.OVERVIEW.HEALTH')"
                class="reset-base w-auto h-auto py-0.5 mb-0 leading-tight ltr:pl-1 rtl:pr-1 text-sm bg-transparent rounded appearance-none cursor-pointer outline-none text-n-slate-12 focus:outline focus:outline-1 focus:outline-n-brand"
                @change="updateHealth"
              >
                <option v-for="opt in HEALTH_OPTIONS" :key="opt" :value="opt">
                  {{ t(`PROJECTS.HEALTH.${opt.toUpperCase()}`) }}
                </option>
              </select>
            </span>
          </div>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('PROJECTS.FIELDS.PRIORITY') }}
            </span>
            <span class="text-sm text-n-slate-12">{{ priorityLabel }}</span>
          </div>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('PROJECTS.FIELDS.START_DATE') }}
            </span>
            <span class="text-sm tabular-nums text-n-slate-12">
              {{ fmtDate(project.start_date) }}
            </span>
          </div>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('PROJECTS.FIELDS.DUE_DATE') }}
            </span>
            <span class="text-sm tabular-nums text-n-slate-12">
              {{ fmtDate(targetDate) }}
            </span>
          </div>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('PROJECTS.OVERVIEW.BUDGET') }}
            </span>
            <span class="text-sm tabular-nums text-n-slate-12">
              {{ budgetLabel }}
            </span>
          </div>

          <div class="flex items-center justify-between gap-3 min-w-0">
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ t('PROJECTS.OVERVIEW.EMPRESA') }}
            </span>
            <button
              v-if="empresa"
              type="button"
              class="inline-flex items-center gap-1 min-w-0 text-sm truncate text-n-blue-text hover:underline"
              @click="openEmpresa"
            >
              <span class="truncate">{{ empresa.name }}</span>
              <span class="i-lucide-external-link size-3.5 shrink-0" />
            </button>
            <span v-else class="text-sm text-n-slate-12">—</span>
          </div>

          <div class="flex items-center justify-between gap-3 min-w-0">
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ t('PROJECTS.OVERVIEW.OWNER') }}
            </span>
            <span class="text-sm truncate text-n-slate-12">{{
              ownerName
            }}</span>
          </div>

          <div class="flex items-center justify-between gap-3 min-w-0">
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ t('PROJECTS.OVERVIEW.SPONSOR') }}
            </span>
            <span class="text-sm truncate text-n-slate-12">
              {{ sponsorName }}
            </span>
          </div>
        </div>

        <!-- Baselines (collapsible, same width as Details) -->
        <ProjectBaselines :project-id="projectId" collapsible />
      </div>
    </aside>
  </div>
</template>
